import produce from 'immer';
import { handleActions } from 'redux-actions';
import { filter, includes, last } from 'lodash';
import actions from './actions';
import { updateStoreWithPath } from '../../../lib/common';
import {
  REQUIRED_OPTION_ITEM_TITLE_BRAND_LAYOUT_TYPE_LIST,
  WIDGET_ITEM_BANNER_ATTACHMENT_TYPE,
  WIDGET_SAVE_RESULT,
  WIDGET_TYPE,
} from '../../../constants/service/app/widgetConstants';
import moment, { setDateFormat } from '../../../lib/moment';
import { DATE_FORMAT } from '../../../constants/service/common/utilConstants';
import { setWidgetProductCondition, setWidgetProductRandomItemCountSelectList } from '../../../lib/utils/widgetUtils';
import { _LIST_PAGINATION_MODEL } from '../app/models';
import {
  WIDGET_ALL_LIST_SEARCH_DATA_MODEL,
  WIDGET_ALL_LIST_SEARCH_OPTION_MODEL,
  WIDGET_DETAIL_MODEL,
  WIDGET_CONNECTED_EVENT_INFO,
  WIDGET_INFO_MODEL,
  WIDGET_ITEM_LIST_FILTER_OPTION_MODEL,
  WIDGET_ITEM_LIST_SORT_OPTION_MODEL,
  WIDGET_PRODUCT_CONDITION_MODEL,
  WIDGET_PRODUCT_LAYOUT_INFO_MODEL,
  WIDGET_SEARCH_KEYWORD,
  WIDGET_SECTION_MODEL,
} from './models';

/**
 *  INITIAL STATE
 */
const initialState = {
  /**
   *  위젯 등록, 편집, 복사
   */
  widgetSave: {
    /**
     *  위젯 정보
     */
    widgetInfo: WIDGET_INFO_MODEL,

    /**
     *  위젯 아이템 리스트
     */
    widgetItemList: {
      /**
       * 리스트
       */
      list: [],
      /**
       *  필터 옵션
       */
      filterOption: WIDGET_ITEM_LIST_FILTER_OPTION_MODEL[WIDGET_TYPE.PRODUCT],
      /**
       *  정렬 옵션
       */
      sortOption: WIDGET_ITEM_LIST_SORT_OPTION_MODEL[WIDGET_TYPE.PRODUCT],
      /**
       * pagination
       */
      pagination: _LIST_PAGINATION_MODEL,
    },

    /**
     *  선택된 위젯 아이템 리스트
     */
    selectedWidgetItemList: {
      list: [],
      idList: [],
    },

    /**
     *  홈에 노출될 위젯 아이템 리스트
     */
    exposureWidgetItemList: {
      list: [],
      idList: [],
    },

    /**
     * 상품 위젯 - 기획전 연결 정보
     */
    connectedEventInfo: WIDGET_CONNECTED_EVENT_INFO,

    /**
     *  위젯 상품 타입 정보
     */
    widgetProductTypeInfo: {
      /**
       *  위젯 상품 조건 사용 여부
       */
      isUsedProductCondition: false,

      /**
       *  위젯 상품 조건
       */
      productCondition: WIDGET_PRODUCT_CONDITION_MODEL,

      /**
       *  위젯 상품 레이아웃 정보
       */
      productLayoutInfo: WIDGET_PRODUCT_LAYOUT_INFO_MODEL,
    },

    /**
     *  상품 1차 카테고리 리스트
     */
    mainProductCategorySelectList: [],
    /**
     *  상품 2차 카테고리 리스트
     */
    subProductCategorySelectList: {
      [null]: [],
    },

    /**
     *  위젯 상세
     */
    widgetDetail: WIDGET_DETAIL_MODEL,

    /**
     *  위젯 섹션
     */
    widgetSection: WIDGET_SECTION_MODEL,

    /**
     *  저장 결과
     */
    result: WIDGET_SAVE_RESULT.NONE,
  },

  /**
   * 위젯 홈 메뉴
   */
  homeMenu: [],

  /**
   * ImgUploadInput용
   */
  tempImgList: [],

  /**
   *  서브 페이지 등록/편집
   */
  subPageSave: {
    /**
     *  페이지 제목
     */
    title: null,
    /**
     *  메모
     */
    memo: null,
  },

  /**
   *  서브 페이지 리스트
   */
  subPageList: {
    /**
     *  리스트
     */
    list: [],
    /**
     *  검색 박스
     */
    searchData: WIDGET_ALL_LIST_SEARCH_DATA_MODEL,
    /**
     *  검색 옵션
     */
    searchOption: WIDGET_ALL_LIST_SEARCH_OPTION_MODEL,
    /**
     *  리스트 pagination
     */
    pagination: _LIST_PAGINATION_MODEL,
  },

  /**
   *  페이지 위젯 리스트
   */
  pageWidgetList: {
    /**
     *  위젯 노출기간 일괄 입력 데이터
     */
    widgetExposurePeriodBatchInfo: {
      /**
       *  위젯 노출 시작일시
       */
      startDate: setDateFormat(moment(), DATE_FORMAT.FULL_DASH_FORMAT),
      /**
       *  위젯 노출 종료일시
       */
      endDate: setDateFormat(moment().add(7, 'day'), DATE_FORMAT.FULL_DASH_FORMAT),
    },

    /**
     *  위젯 리스트
     */
    widgetList: {
      /**
       *  리스트
       */
      list: [],
    },

    /**
     *  선택된 페이지 위젯 id 리스트
     */
    selectedWidgetIdList: [],

    /**
     *  오리지널 페이지 위젯 리스트
     */
    originalWidgetList: [],

    /**
     *  페이지 정보
     */
    widgetPageInfo: {
      /**
       *  페이지 ID
       */
      id: null,
      /**
       *  페이지 제목
       */
      title: null,
      /**
       *  메모
       */
      memo: null,
    },
  },

  /**
   *  위젯 전체 리스트
   */
  widgetAllList: {
    /**
     *  위젯 노출기간 일괄 입력 데이터
     */
    widgetExposurePeriodBatchInfo: {
      /**
       *  위젯 노출 시작일시
       */
      startDate: setDateFormat(moment(), DATE_FORMAT.FULL_DASH_FORMAT),
      /**
       *  위젯 노출 종료일시
       */
      endDate: setDateFormat(moment().add(7, 'day'), DATE_FORMAT.FULL_DASH_FORMAT),
    },

    /**
     *  위젯 리스트
     */
    widgetList: {
      /**
       *  리스트
       */
      list: [],
      /**
       *  검색 박스
       */
      searchOption: WIDGET_ALL_LIST_SEARCH_DATA_MODEL,
      /**
       *  리스트 pagination
       */
      pagination: _LIST_PAGINATION_MODEL,
    },

    /**
     *  선택된 위젯 id 리스트
     */
    selectedWidgetIdList: [],

    /**
     *  오리지널 위젯 리스트
     */
    originalWidgetList: [],
  },

  /**
   *  위젯 미리보기 모달
   */
  widgetPreviewModal: {
    isOpen: false,
    data: {},
  },

  /**
   *  변경된 위젯 비교하는 모달
   */
  widgetPreviewCompareModal: {
    isOpen: false,
    data: {},
  },

  /**
   *  비활성 중인 위젯 리스트 검색 모달
   */
  searchInactiveWidgetModal: {
    isOpen: false,
    data: {
      list: [],
      searchOption: {
        searchKeyword: WIDGET_SEARCH_KEYWORD,
      },
      pagination: _LIST_PAGINATION_MODEL,
      isProductRecommend: !window.location.pathname.includes('/sub/'),
    },
  },

  /**
   *  위젯 레이아웃 메뉴얼 모달
   */
  widgetLayoutManualModal: {
    isOpen: false,
    data: {},
  },

  /**
   *  브랜드 리스트 검색 모달
   */
  searchStoreListModal: {
    isOpen: false,
    data: {},
  },

  /**
   *  기획전 리스트 검색 모달
   */
  searchEventListModal: {
    isOpen: false,
    data: {},
  },

  /**
   *  상품 리스트 검색 모달
   */
  searchProductListModal: {
    isOpen: false,
    data: {},
  },

  /**
   *  후기 리스트 검색 모달
   */
  searchReviewListModal: {
    isOpen: false,
    data: {},
  },

  /**
   *  태그 리스트 검색 모달
   */
  searchTagListModal: {
    isOpen: false,
    data: {},
  },

  /**
   *  위젯에 노출중인 기획전 미리보기
   */
  previewExposureEventModal: {
    isOpen: false,
    data: {},
  },

  /**
   *  홈 리스트 검색 모달
   */
  searchHomeListModal: {
    isOpen: false,
    data: {},
  },

  /**
   * 링크ID선택 임시데이터 저장
   */
  tempModalData: {},
};

/**
 *  REDUCER
 */
export default handleActions(
  {
    /**
     *  [위젯] 위젯 데이터 변경하기
     */
    [actions.SET_DATA]: (state, action) => {
      return produce(state, (draft) => {
        updateStoreWithPath(action.payload.path, action.payload.value, draft);
      });
    },

    /**
     *  [위젯 삭제] 위젯 삭제 요청하기 성공
     */
    [actions.DELETE_WIDGET_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        draft.widgetAllList.widgetList.list = filter(
          draft.widgetAllList.widgetList.list,
          (item) => item.id !== action.payload,
        );

        draft.widgetAllList.originalWidgetList = filter(
          draft.widgetAllList.originalWidgetList,
          (item) => item.id !== action.payload,
        );
      });
    },

    /**
     * [위젯 등록] 위젯에 기획전 연결하기 토글
     */
    [actions.TOGGLE_WIDGET_TO_EVENT]: (state, action) => {
      return produce(state, (draft) => {
        draft.widgetSave.widgetInfo.isActiveEventConnect = action.payload;
      });
    },

    /**
     * [위젯 등록] 위젯 버튼 사용여부 토글
     */
    [actions.TOGGLE_WIDGET_BUTTON]: (state, action) => {
      return produce(state, (draft) => {
        draft.widgetSave.widgetInfo.isUsedButton = action.payload;
      });
    },

    /**
     *  [위젯 배너 등록] (상품, 브랜드, 기획전, 배너) 아이템 리스트 받기
     */
    [actions.GET_WIDGET_BANNER_ITEM_LINK_ID_ITEM_LIST_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        updateStoreWithPath([...action.payload.listPath], action.payload.listData, draft);

        /**
         *  선택된 기획전이 있을 경우
         */
        if (action.payload.selectedItem && action.payload.listPath.length) {
          updateStoreWithPath(
            [...[...action.payload.listPath].slice(0, -1), 'selectedItem'],
            action.payload.selectedItem,
            draft,
          );
        }
      });
    },

    /**
     *  [서브 페이지] 서브 페이지 리스트 요청하기 성공
     */
    [actions.GET_SUB_PAGE_LIST_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        draft.subPageList = {
          ...draft.subPageList,
          ...action.payload.subPageList,
        };
      });
    },

    /**
     *  [서브 페이지 상태] 서브 페이지 상태 업데이트 요청하기 성공
     */
    [actions.UPDATE_SUB_PAGE_STATUS_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        draft.subPageList.list[action.payload.index].status = action.payload.status;
      });
    },

    /**
     *  [서브 페이지 삭제] 서브 페이지 삭제 요청하기 성공
     */
    [actions.DELETE_SUB_PAGE_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        draft.subPageList.list = filter(draft.subPageList.list, (item) => item.id !== action.payload.subPageId);
      });
    },

    /**
     *  [메인 페이지 위젯 리스트] 메인 페이지 위젯 리스트 요청하기 성공
     */
    [actions.GET_MAIN_PAGE_WIDGET_LIST_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        if (action.payload.pageWidgetList[0]) {
          // 첫 번째 위젯은 순서를 변경못하도록 설정 (기획전 위젯)
          action.payload.pageWidgetList[0].rowCanDrag = false;
        }

        // 메인 페이지에서 홈 메뉴 row 추가 (순서 변경 불가)
        action.payload.pageWidgetList.splice(1, 0, {
          id: null,
          rowCanDrag: false,
          isHomeMenu: true,
        });
        draft.pageWidgetList.widgetList.list = action.payload.pageWidgetList;
        draft.pageWidgetList.originalWidgetList = action.payload.pageWidgetList;

        /**
         *  선택된 위젯 초기화
         */
        draft.pageWidgetList.selectedWidgetIdList = [];
      });
    },

    /**
     *  [서브 페이지 위젯 리스트] 서브 페이지 위젯 리스트 요청하기 성공
     */
    [actions.GET_SUB_PAGE_WIDGET_LIST_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        draft.pageWidgetList.widgetList.list = action.payload.pageWidgetList;
        draft.pageWidgetList.originalWidgetList = action.payload.pageWidgetList;

        draft.pageWidgetList.widgetPageInfo = action.payload.pageInfo;
        draft.pageWidgetList.originalWidgetPageInfo = action.payload.pageInfo;

        /**
         *  선택된 위젯 초기화
         */
        draft.pageWidgetList.selectedWidgetIdList = [];
      });
    },

    /**
     *  [위젯 전체 리스트] 위젯 전체 리스트 요청하기 성공
     */
    [actions.GET_WIDGET_ALL_LIST_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        draft.widgetAllList.widgetList = {
          ...draft.widgetAllList.widgetList,
          ...action.payload.widgetList,
        };
        draft.widgetAllList.originalWidgetList = action.payload.widgetList.list;

        /**
         *  선택된 위젯 초기화
         */
        draft.widgetAllList.selectedWidgetIdList = [];
      });
    },

    /**
     *  [위젯 비활성 리스트] 위젯 비활성 리스트 요청하기 성공
     */
    [actions.GET_WIDGET_INACTIVE_LIST_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        draft.searchInactiveWidgetModal.data = {
          ...draft.searchInactiveWidgetModal.data,
          ...action.payload.widgetInactiveList,
        };
      });
    },

    /**
     *  [위젯 상세] 위젯 상세 데이터 요청하기 성공
     */
    [actions.GET_WIDGET_DETAIL_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        const { eventId, eventMainTitle, eventStartDate, eventEndDate, eventStatus, ...widgetInfo } =
          action.payload.widgetData.widgetInfo;
        draft.widgetSave.widgetInfo = widgetInfo;
        if (typeof eventId === 'number') {
          draft.widgetSave.widgetInfo.isActiveEventConnect = true;
        }
        draft.widgetSave.selectedWidgetItemList = action.payload.widgetData.selectedWidgetItemList;
        draft.widgetSave.exposureWidgetItemList = action.payload.widgetData.exposureWidgetItemList;
        draft.widgetSave.widgetProductTypeInfo = action.payload.widgetData.widgetProductTypeInfo;
        draft.widgetSave.widgetDetail = action.payload.widgetData.widgetDetail;
        draft.widgetSave.connectedEventInfo = {
          linkId: eventId,
          linkName: eventMainTitle,
          eventPeriod: {
            startDate: eventStartDate,
            endDate: eventEndDate,
          },
          status: eventStatus,
        };

        draft.widgetSave.widgetSection = { ...WIDGET_SECTION_MODEL };

        /**
         *  위젯 타입이 상품인 경우
         */
        if (draft.widgetSave.widgetInfo.widgetType === WIDGET_TYPE.PRODUCT) {
          /**
           *  위젯 상품 조건 설정이 되었을 경우
           *  위젯 상품 조건을 설정
           */
          if (draft.widgetSave.widgetProductTypeInfo.productCondition) {
            /**
             *  선택된 위젯 아이템 리스트
             */
            draft.widgetSave.selectedWidgetItemList.list = setWidgetProductCondition(
              draft.widgetSave.selectedWidgetItemList.list,
              draft.widgetSave.widgetProductTypeInfo.productCondition,
            );

            /**
             *  홈에 노출될 위젯 아이템 리스트
             */
            if (draft.widgetSave.exposureWidgetItemList.list.length) {
              draft.widgetSave.exposureWidgetItemList.list = setWidgetProductCondition(
                draft.widgetSave.exposureWidgetItemList.list,
                draft.widgetSave.widgetProductTypeInfo.productCondition,
              );
            }
          }

          /**
           *  위젯 아이템 랜덤 노출일 경우
           */
          if (draft.widgetSave.widgetProductTypeInfo.productLayoutInfo.isRandomItem) {
            draft.widgetSave.widgetProductTypeInfo.productLayoutInfo.exposureItemCountSelectList =
              setWidgetProductRandomItemCountSelectList(draft.widgetSave.widgetInfo.layoutType);
          }
        } else if (
          /**
           *  위젯 타입이 브랜드 또는 배너인경우
           *  위젯 섹션 페이지의 마지막 탭을 확성화함.
           */
          draft.widgetSave.widgetInfo.widgetType === WIDGET_TYPE.BRAND ||
          draft.widgetSave.widgetInfo.widgetType === WIDGET_TYPE.BANNER
        ) {
          const targetTabItem = last(draft.widgetSave.exposureWidgetItemList.list);
          draft.widgetSave.widgetSection.currentTabKey = targetTabItem ? targetTabItem.id : null;
        }

        /**
         *  위젯 타입이 브랜드인 경우, 아이템 타이틀 사용여부 조건 설정
         */
        if (
          draft.widgetSave.widgetInfo.widgetType === WIDGET_TYPE.BRAND &&
          !includes(REQUIRED_OPTION_ITEM_TITLE_BRAND_LAYOUT_TYPE_LIST, draft.widgetSave.widgetInfo.layoutType)
        ) {
          draft.widgetSave.widgetSection.isUsedTitleItem = !!draft.widgetSave.exposureWidgetItemList.list.filter(
            (item) => item.title,
          ).length;
        }

        /**
         *  위젯 배너 아이템 리스트에서 각 위젯 배너 아이템에 APP 과 PC 이미지가 없을 경우 빈 배열 형태 만들기
         *  해당 작업을 하지 않을 경우, PC 이미지가 없는 배너를 가지고 올때 문제가 발생함
         */
        if (draft.widgetSave.widgetInfo.widgetType === WIDGET_TYPE.BANNER) {
          draft.widgetSave.selectedWidgetItemList.list.forEach((item) => {
            if (!item.img[WIDGET_ITEM_BANNER_ATTACHMENT_TYPE.APP]) {
              item.img[WIDGET_ITEM_BANNER_ATTACHMENT_TYPE.APP] = [];
            } else if (!item.img[WIDGET_ITEM_BANNER_ATTACHMENT_TYPE.PC]) {
              item.img[WIDGET_ITEM_BANNER_ATTACHMENT_TYPE.PC] = [];
            }
          });

          draft.widgetSave.exposureWidgetItemList.list.forEach((item) => {
            if (!item.img[WIDGET_ITEM_BANNER_ATTACHMENT_TYPE.APP]) {
              item.img[WIDGET_ITEM_BANNER_ATTACHMENT_TYPE.APP] = [];
            } else if (!item.img[WIDGET_ITEM_BANNER_ATTACHMENT_TYPE.PC]) {
              item.img[WIDGET_ITEM_BANNER_ATTACHMENT_TYPE.PC] = [];
            }
          });
        }

        /**
         *  위젯 상품 조건 설정이 되지 않았을 경우,
         *  또는 위젯 타입이 상품 타입이 아닌 경우,
         *  위젯 상품 조건 초기값을 설정함
         */
        if (
          !draft.widgetSave.widgetProductTypeInfo.productCondition ||
          draft.widgetSave.widgetInfo.widgetType !== WIDGET_TYPE.PRODUCT
        ) {
          draft.widgetSave.widgetProductTypeInfo.productCondition = WIDGET_PRODUCT_CONDITION_MODEL;
        }

        /**
         *  받아온 위젯 아이템 중에 위젯 상세에 설정된 위젯 아이템인 경우에는
         *  위젯 아이템 선택 부분에서 이동을 할 수 없도록 하기
         */
        draft.widgetSave.widgetItemList.list = draft.widgetSave.widgetItemList.list.map((item) => ({
          ...item,
          uncheckable: includes(draft.widgetSave.selectedWidgetItemList.idList, item.id) || item.uncheckable,
        }));
      });
    },

    /**
     * 위젯 홈 메뉴 가져오기
     */
    [actions.GET_HOME_MENU_WIDGET_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        draft.homeMenu = action.payload.homeMenu;
      });
    },

    /**
     * 업데이트 된 홈 메뉴
     */
    [actions.UPDATE_HOME_MENU_WIDGET_SUCCESS]: (state, action) => {
      return produce(state, (draft) => {
        const index = draft.homeMenu.findIndex((item) => item.id === action.payload.homeMenuItem.id);
        if (index !== -1)
          draft.homeMenu[index] = {
            ...action.payload.homeMenuItem,
            isUpdated: true,
          };
      });
    },

    /**
     *  모달 토글
     */
    [actions.TOGGLE_MODAL]: (state, action) => {
      return produce(state, (draft) => {
        updateStoreWithPath([action.payload.name, 'data'], action.payload.data, draft);
        updateStoreWithPath([action.payload.name, 'isOpen'], action.payload.value, draft);
      });
    },
    /**
     *  [위젯] 위젯 링크 타입 데이터 변경하기
     */
    [actions.SET_WIDGET_LINK_TYPE]: (state, action) => {
      return produce(state, (draft) => {
        draft.widgetSave.exposureWidgetItemList.list[action.payload.index].linkType = action.payload.type;
        draft.widgetSave.exposureWidgetItemList.list[action.payload.index].linkId = action.payload.id;
      });
    },
    /**
     * [위젯] 위젯 탭 키 변경하기
     */
    [actions.SET_WIDGET_CURRENT_TAB_KEY]: (state, action) => {
      return produce(state, (draft) => {
        draft.widgetSave.widgetSection.currentTabKey = action.payload.value;
      });
    },

    [actions.TOGGLE_WIDGET_COUPON_USE_STATUS]: (state, action) => {
      return produce(state, (draft) => {
        const targetExposureList = state.widgetSave.exposureWidgetItemList.list;
        draft.widgetSave.widgetInfo.isUseCoupon = action.payload;
        if (!action.payload && targetExposureList.length) {
          draft.widgetSave.exposureWidgetItemList.list = targetExposureList?.map((widget) => ({
            ...widget,
            coupon: null,
          }));
        }
      });
    },

    /**
     *  [위젯] 위젯 쿠폰 변경하기
     */
    [actions.SET_WIDGET_COUPON]: (state, action) => {
      return produce(state, (draft) => {
        const { index, ...coupon } = action.payload;
        const targetExposureItem = state.widgetSave.exposureWidgetItemList.list[index];
        const nextData = {
          productId: null,
          productName: null,
          linkName: null,
          storeId: null,
          storeName: null,
          originPrice: null,
          discountPrice: null,
          discountRate: null,
          salesPrice: null,
          imageUrl: null,
          discountStartDate: null,
          discountEndDate: null,
          createDate: null,
        };

        //미리보기 상품 정보 초기화
        draft.widgetSave.exposureWidgetItemList.list[index] = { ...targetExposureItem, coupon, ...nextData };
      });
    },

    /**
     *  [위젯] 위젯 상품 변경하기
     */
    [actions.SET_WIDGET_PRODUCT]: (state, action) => {
      return produce(state, (draft) => {
        const targetExposureItem = state.widgetSave.exposureWidgetItemList.list[action.payload.index];
        const nextData = {
          productId: action.payload.id,
          productName: action.payload.name,
          linkName: action.payload.linkName,
          storeId: action.payload.brandId,
          storeName: action.payload.brandName,
          originPrice: action.payload.originPrice,
          discountPrice: action.payload.discountPrice,
          discountRate: action.payload.discountRate,
          salesPrice: action.payload.salesPrice,
          imageUrl: action.payload.url,
          discountStartDate: action.payload.discountStartDate,
          discountEndDate: action.payload.discountEndDate,
          createDate: action.payload.createDate,
        };

        //미리보기 상품 정보 설정
        draft.widgetSave.exposureWidgetItemList.list[action.payload.index] = { ...targetExposureItem, ...nextData };
      });
    },
    /**
     *  [위젯] 위젯 메모 변경하기
     */
    [actions.SET_WIDGET_MEMO]: (state, action) => {
      return produce(state, (draft) => {
        draft.widgetSave.exposureWidgetItemList.list[action.payload.index].memo = action.payload.value;
      });
    },
    /**
     *  [위젯] 위젯 전체 리스트 설정
     */
    [actions.SET_WIDGET_ALL_LIST]: (state, action) => {
      return produce(state, (draft) => {
        draft.widgetAllList.widgetList.list = action.payload;
      });
    },
    /**
     *  [위젯] 위젯 검색 > 위젯 타입 변경하기
     *  위젯 타입 별로 위젯 레이아웃이 다르기 때문에
     *  위젯 타입이 변경될 때마다 위젯 레이아웃은 초기화를 해야함
     */
    [actions.SET_SEARCH_OPTION_WIDGET_TYPE]: (state, action) => {
      return produce(state, (draft) => {
        draft.widgetAllList.widgetList.searchOption.widgetType = action.payload;
        draft.widgetAllList.widgetList.searchOption.layoutType = null;
      });
    },
    /**
     *  [위젯] 위젯 검색 > 위젯 레이아웃 타입 변경하기
     */
    [actions.SET_SEARCH_OPTION_WIDGET_LAYOUT_TYPE]: (state, action) => {
      return produce(state, (draft) => {
        draft.widgetAllList.widgetList.searchOption.layoutType = action.payload;
      });
    },
    /**
     *  [위젯] 위젯 검색 > 위젯 검색 타입 변경하기
     */
    [actions.SET_SEARCH_OPTION_KEYWORD_TYPE]: (state, action) => {
      return produce(state, (draft) => {
        draft.widgetAllList.widgetList.searchOption.layoutType = action.payload;
      });
    },
    /**
     *  [위젯] 위젯 검색 > 위젯 검색 타입 변경하기
     */
    [actions.SET_SEARCH_OPTION_KEYWORD_TYPE]: (state, action) => {
      return produce(state, (draft) => {
        draft.widgetAllList.widgetList.searchOption.searchKeyword.type = action.payload;
      });
    },
    /**
     *  [위젯] 위젯 검색 > 위젯 검색어 변경하기
     */
    [actions.SET_SEARCH_OPTION_KEYWORD]: (state, action) => {
      return produce(state, (draft) => {
        draft.widgetAllList.widgetList.searchOption.searchKeyword.text = action.payload;
      });
    },
    /**
     *  [위젯] 위젯 검색 > 위젯 노출 기간 타입 변경하기
     */
    [actions.SET_SEARCH_OPTION_EXPOSURE_PERIOD_TYPE]: (state, action) => {
      return produce(state, (draft) => {
        draft.widgetAllList.widgetList.searchOption.exposurePeriod.type = action.payload;
      });
    },
    /**
     *  [위젯] 위젯 노출기간 일괄 입력 데이터 변경하기
     */
    [actions.SET_PERIOD_BATCH_INFO]: (state, action) => {
      return produce(state, (draft) => {
        draft.widgetAllList.widgetExposurePeriodBatchInfo = action.payload;
      });
    },
    /**
     *  [위젯] 위젯 노출기간 일괄 입력 데이터 변경하기
     */
    [actions.SET_SELECTED_WIDGET_ID_LIST]: (state, action) => {
      return produce(state, (draft) => {
        draft.widgetAllList.selectedWidgetIdList = action.payload;
      });
    },
    /**
     *  [위젯] 위젯 검색 > 위젯 노출 기간 변경하기
     */
    [actions.SET_SEARCH_OPTION_EXPOSURE_PERIOD]: (state, action) => {
      return produce(state, (draft) => {
        draft.widgetAllList.widgetList.searchOption.exposurePeriod.period = action.payload;
      });
    },
    /**
     *  [위젯] 위젯 검색 > 위젯 생성일 변경하기
     */
    [actions.SET_SEARCH_OPTION_CREATE_DATE]: (state, action) => {
      return produce(state, (draft) => {
        draft.widgetAllList.widgetList.searchOption.createDate = action.payload;
      });
    },
    /**
     *  [위젯] 검색 데이터 초기화
     */
    [actions.RESET_WIDGET_LIST_SEARCH_DATA]: (state) => {
      return produce(state, (draft) => {
        draft.widgetAllList.widgetList.searchOption = WIDGET_ALL_LIST_SEARCH_DATA_MODEL;
      });
    },
  },
  initialState,
);
